import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Edit2, PackageCheck, XCircle, Truck, Check, X, ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr } from "./lib";

const STATUS_META = {
  ordered:              { label: "Ordered",              chip: "bg-[#a8dadc]/25 text-[#457b9d]" },
  partially_received:   { label: "Partially Received",   chip: "bg-[#FEF0C7] text-[#B54708]" },
  received:             { label: "Received",             chip: "bg-[#12B76A]/12 text-[#0F9256]" },
  cancelled:            { label: "Cancelled",            chip: "bg-[#98A2B3]/20 text-[#475467]" },
};

export default function PurchaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiveOpen, setReceiveOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/spares/purchases/${id}`);
      setP(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load purchase");
      nav("/spare-parts/purchases");
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (!window.confirm("Cancel this purchase? This cannot be undone.")) return;
    try {
      await api.post(`/spares/purchases/${id}/cancel`);
      toast.success("Purchase cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  if (loading || !p) return <div className="py-10 text-center text-[13px] text-[#667085]">Loading…</div>;

  const meta = STATUS_META[p.status] || STATUS_META.ordered;
  const canReceive = p.status === "ordered" || p.status === "partially_received";
  const canEdit = p.status === "ordered" && (p.lines || []).every((ln) => !ln.received_qty);

  return (
    <div className="space-y-4" data-testid="purchase-detail-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/spare-parts/purchases" data-testid="purchase-detail-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight font-mono">{p.number}</h1>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${meta.chip}`}
                    data-testid="purchase-detail-status">
                {meta.label}
              </span>
            </div>
            <div className="text-[12.5px] text-[#667085] mt-0.5">
              {p.vendor_snapshot?.name || "—"}
              {p.invoice_number && <> · Inv <span className="font-mono">{p.invoice_number}</span></>}
              {p.invoice_date && <> · {new Date(p.invoice_date).toLocaleDateString()}</>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canReceive && (
            <button onClick={() => setReceiveOpen(true)} data-testid="purchase-detail-receive"
                    className="h-10 px-4 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
              <PackageCheck size={13} /> Receive
            </button>
          )}
          {canEdit && (
            <button onClick={() => nav(`/spare-parts/purchases/${p.id}/edit`)} data-testid="purchase-detail-edit"
                    className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
              <Edit2 size={13} /> Edit
            </button>
          )}
          {p.status !== "received" && p.status !== "cancelled" && (
            <button onClick={cancel} data-testid="purchase-detail-cancel"
                    className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#e63946] hover:bg-[#e63946]/10 inline-flex items-center gap-1.5">
              <XCircle size={13} /> Cancel
            </button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <section className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#F1FAEE]">
              <h3 className="text-[13px] font-bold text-[#1d3557]">Line items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="purchase-lines-table">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                    <th className="px-4 py-2.5">Part</th>
                    <th className="px-3 py-2.5 text-right">Qty</th>
                    <th className="px-3 py-2.5 text-right">Received</th>
                    <th className="px-3 py-2.5 text-right">Rate</th>
                    <th className="px-3 py-2.5 text-right">GST</th>
                    <th className="px-3 py-2.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(p.lines || []).map((ln) => {
                    const recv = Number(ln.received_qty || 0);
                    const full = recv >= Number(ln.qty || 0) - 1e-6;
                    return (
                      <tr key={ln.id} className="border-b border-[#F1FAEE]">
                        <td className="px-4 py-2.5">
                          <div className="font-semibold text-[#1d3557]">{ln.name}</div>
                          {ln.part_number && <div className="text-[11px] font-mono text-[#667085]">{ln.part_number}</div>}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{ln.qty}</td>
                        <td className={`px-3 py-2.5 text-right font-mono tabular-nums ${full ? "text-[#0F9256]" : recv > 0 ? "text-[#B54708]" : "text-[#667085]"}`}>
                          {recv || "—"}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono tabular-nums">{inr(ln.rate)}</td>
                        <td className="px-3 py-2.5 text-right text-[#667085]">{ln.gst_rate || 0}%</td>
                        <td className="px-3 py-2.5 text-right font-mono tabular-nums font-bold">{inr(ln.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {(p.receipts || []).length > 0 && (
            <section className="bg-white rounded-xl border border-[#E4E7EC] p-4">
              <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Receipts ({p.receipts.length})</h3>
              <ul className="divide-y divide-[#F1FAEE]" data-testid="purchase-receipts-list">
                {p.receipts.map((r) => (
                  <li key={r.id} className="py-2 flex items-center gap-3">
                    <Truck size={14} className="text-[#457b9d]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-mono font-semibold text-[#1d3557]">{r.number}</div>
                      <div className="text-[11px] text-[#667085]">
                        {new Date(r.created_at).toLocaleString()} · {(r.lines || []).length} line{(r.lines || []).length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="text-right text-[12px] font-mono text-[#0F9256]">
                      +{(r.lines || []).reduce((s, l) => s + Number(l.qty || 0), 0)}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {p.notes && (
            <section className="bg-[#F1FAEE] border border-[#a8dadc]/40 rounded-xl p-3 text-[12.5px] text-[#1d3557]">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#457b9d] mb-1">Notes</div>
              {p.notes}
            </section>
          )}
        </div>

        <aside>
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 sticky top-4" data-testid="purchase-detail-totals">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Totals</h3>
            <Row label="Subtotal" value={inr(p.totals?.subtotal)} />
            {p.totals?.line_discount ? <Row label="Line discount" value={`− ${inr(p.totals.line_discount)}`} muted /> : null}
            <Row label="Taxable" value={inr(p.totals?.taxable)} />
            {p.is_interstate ? (
              <Row label="IGST" value={inr(p.totals?.igst)} muted />
            ) : (
              <>
                <Row label="CGST" value={inr(p.totals?.cgst)} muted />
                <Row label="SGST" value={inr(p.totals?.sgst)} muted />
              </>
            )}
            {p.extra_discount ? <Row label="Extra discount" value={`− ${inr(p.extra_discount)}`} muted /> : null}
            <div className="my-2 border-t border-[#F1FAEE]" />
            <Row label="Grand total" value={inr(p.totals?.grand_total)} big />
          </section>
        </aside>
      </div>

      {receiveOpen && (
        <ReceiveDialog p={p} onClose={() => setReceiveOpen(false)}
                       onSaved={() => { setReceiveOpen(false); load(); }} />
      )}
    </div>
  );
}

function Row({ label, value, muted, big }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${big ? "text-[15px] font-bold" : "text-[12.5px]"}`}>
      <span className={muted ? "text-[#667085]" : "text-[#1d3557]"}>{label}</span>
      <span className={`font-mono tabular-nums ${muted ? "text-[#667085]" : "text-[#1d3557]"}`}>{value}</span>
    </div>
  );
}

function ReceiveDialog({ p, onClose, onSaved }) {
  const initial = (p.lines || []).map((ln) => ({
    line_id: ln.id,
    name: ln.name,
    part_number: ln.part_number || "",
    ordered: Number(ln.qty || 0),
    already: Number(ln.received_qty || 0),
    receive_now: Math.max(0, Number(ln.qty || 0) - Number(ln.received_qty || 0)),
  }));
  const [items, setItems] = useState(initial);
  const [invoice, setInvoice] = useState(p.invoice_number || "");
  const [invDate, setInvDate] = useState(p.invoice_date || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    const payload = items
      .filter((it) => Number(it.receive_now) > 0)
      .map((it) => ({ line_id: it.line_id, qty: Number(it.receive_now) }));
    if (payload.length === 0) { setError("Enter at least one quantity to receive"); return; }
    setSaving(true); setError(null);
    try {
      await api.post(`/spares/purchases/${p.id}/receive`, {
        items: payload, invoice_number: invoice || "", invoice_date: invDate || null, notes,
      });
      toast.success("Stock received");
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not receive");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="receive-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
           data-testid="receive-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <div>
            <h2 className="text-[16px] font-bold text-[#1d3557]">Receive Stock</h2>
            <p className="text-[11.5px] text-[#667085]">Against {p.number} · {p.vendor_snapshot?.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#E4E7EC] grid place-items-center text-[#667085]"
                  data-testid="receive-dialog-close">
            <X size={17} />
          </button>
        </header>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Vendor invoice #</span>
              <input value={invoice} onChange={(e) => setInvoice(e.target.value)}
                     data-testid="receive-invoice-number"
                     className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Invoice date</span>
              <input type="date" value={invDate || ""} onChange={(e) => setInvDate(e.target.value)}
                     className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </label>
          </div>
          <div className="rounded-xl border border-[#E4E7EC] overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] bg-[#F7F9FC] border-b border-[#F1FAEE]">
                  <th className="px-3 py-2">Part</th>
                  <th className="px-3 py-2 text-right">Ordered</th>
                  <th className="px-3 py-2 text-right">Already</th>
                  <th className="px-3 py-2 text-right">Receive now</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.line_id} className="border-b border-[#F1FAEE] last:border-0" data-testid={`receive-row-${i}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-[#1d3557]">{it.name}</div>
                      {it.part_number && <div className="text-[11px] font-mono text-[#667085]">{it.part_number}</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{it.ordered}</td>
                    <td className="px-3 py-2 text-right font-mono text-[#667085]">{it.already}</td>
                    <td className="px-3 py-2 text-right">
                      <input type="number" min={0} max={it.ordered - it.already}
                             value={it.receive_now}
                             onChange={(e) => {
                               const v = Number(e.target.value) || 0;
                               setItems((rows) => rows.map((r, j) => j === i ? { ...r, receive_now: v } : r));
                             }}
                             data-testid={`receive-row-${i}-qty`}
                             className="w-20 h-8 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono text-right" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Notes</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="receive-error">
              {error}
            </div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
                  data-testid="receive-submit-btn"
                  className="h-10 px-5 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Receiving…" : "Confirm receipt"}
          </button>
        </footer>
      </div>
    </div>
  );
}
